import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, Palette, Edit3, CheckCircle, ArrowRight } from 'lucide-react';

const CustomizeGuidePage = () => {
  const [selectedPalette, setSelectedPalette] = useState('default');

  const colorPalettes = {
    default: [
      { name: 'Career & Vocation', color: '#4CAF50' },
      { name: 'Financial Well-Being', color: '#8BC34A' },
      { name: 'Physical Health', color: '#CDDC39' },
      { name: 'Emotional Well-Being', color: '#FFC107' },
      { name: 'Family', color: '#FF9800' },
      { name: 'Friends', color: '#FF5722' },
      { name: 'Relationships & Love', color: '#E91E63' },
      { name: 'Community & Giving', color: '#9C27B0' },
      { name: 'Fun & Recreation', color: '#673AB7' },
      { name: 'Physical Environment', color: '#3F51B5' },
      { name: 'Personal Growth', color: '#2196F3' },
      { name: 'Spirituality', color: '#00BCD4' }
    ],
    pastels: [
      { name: 'Career & Vocation', color: '#B8E6B8' },
      { name: 'Financial Well-Being', color: '#D4E8B8' },
      { name: 'Physical Health', color: '#FFF9C4' },
      { name: 'Emotional Well-Being', color: '#FFE0B2' },
      { name: 'Family', color: '#FFCCBC' },
      { name: 'Friends', color: '#FFAB91' },
      { name: 'Relationships & Love', color: '#F8BBD0' },
      { name: 'Community & Giving', color: '#E1BEE7' },
      { name: 'Fun & Recreation', color: '#D1C4E9' },
      { name: 'Physical Environment', color: '#C5CAE9' },
      { name: 'Personal Growth', color: '#BBDEFB' },
      { name: 'Spirituality', color: '#B2EBF2' }
    ],
    earthtones: [
      { name: 'Career & Vocation', color: '#8D6E63' },
      { name: 'Financial Well-Being', color: '#A1887F' },
      { name: 'Physical Health', color: '#BCAAA4' },
      { name: 'Emotional Well-Being', color: '#D7CCC8' },
      { name: 'Family', color: '#A1887F' },
      { name: 'Friends', color: '#8D6E63' },
      { name: 'Relationships & Love', color: '#6D4C41' },
      { name: 'Community & Giving', color: '#5D4037' },
      { name: 'Fun & Recreation', color: '#795548' },
      { name: 'Physical Environment', color: '#4E342E' },
      { name: 'Personal Growth', color: '#3E2723' },
      { name: 'Spirituality', color: '#8D6E63' }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm mb-6 border border-green-200">
            <Sparkles className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">Your Life, Your Way!</span>
          </div>
          
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            🎨 Wheel Customization Guide
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Make your Wheel of Life truly <span className="text-green-600 font-semibold">yours</span>! 
            Personalize domains, choose questions that resonate, and create a wheel that reflects your unique journey.
          </p>
        </div>

        {/* How It Works Section */}
        <Card className="p-8 mb-8 bg-white shadow-xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
            <Edit3 className="w-8 h-8 mr-3 text-green-600" />
            How Customization Works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-green-50 p-6 rounded-lg border-2 border-green-200">
              <div className="text-3xl font-bold text-green-600 mb-3">1️⃣</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Choose Base Domain</h3>
              <p className="text-gray-600 text-sm">
                Select which of the 12 life domains should provide the questions. This determines what you'll reflect on!
              </p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
              <div className="text-3xl font-bold text-blue-600 mb-3">2️⃣</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Name It Your Way</h3>
              <p className="text-gray-600 text-sm">
                Give it a custom name that speaks to YOU! "Career" could become "Dream Job" or "Creative Calling" ✨
              </p>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-lg border-2 border-purple-200">
              <div className="text-3xl font-bold text-purple-600 mb-3">3️⃣</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Pick Your Colors</h3>
              <p className="text-gray-600 text-sm">
                Choose colors that inspire you! Use our palette suggestions below or create your own unique scheme.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-lg border-2 border-amber-200">
            <div className="flex items-start space-x-3">
              <Sparkles className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Pro Tip: Mix & Match!</h4>
                <p className="text-gray-700 text-sm">
                  Want "Physical Health" questions but call it "Body Temple"? Done! ✅<br/>
                  Need "Relationships" questions for your "Soul Connections" domain? You got it! 💕<br/>
                  The possibilities are endless - make it meaningful to YOU!
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Example Section */}
        <Card className="p-8 mb-8 bg-gradient-to-br from-green-600 to-green-700 text-white shadow-xl">
          <h2 className="text-3xl font-bold mb-6">💡 Real-Life Example</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur rounded-lg p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-3">Before Customization</h3>
              <div className="space-y-2 text-green-50">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded-full bg-white/30"></div>
                  <span>Domain: "Career & Vocation"</span>
                </div>
                <div className="text-sm pl-6 italic">
                  Questions about general career satisfaction...
                </div>
              </div>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-6 border-2 border-white/40">
              <h3 className="text-xl font-bold mb-3">After Customization ✨</h3>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded-full bg-yellow-300"></div>
                  <span className="font-semibold">Your Domain: "My Creative Studio"</span>
                </div>
                <div className="text-sm pl-6">
                  <strong>Base:</strong> Career & Vocation<br/>
                  <strong>Questions:</strong> Still about fulfillment & growth<br/>
                  <strong>Name:</strong> Reflects YOUR passion! 🎨
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Color Palettes Section */}
        <Card className="p-8 mb-8 bg-white shadow-xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
            <Palette className="w-8 h-8 mr-3 text-green-600" />
            Color Palette Inspiration
          </h2>
          
          <p className="text-gray-600 mb-6">
            Choose a color scheme that resonates with you, or use these as starting points for your own unique palette!
          </p>

          {/* Palette Selector */}
          <div className="flex gap-3 mb-8">
            <button
              onClick={() => setSelectedPalette('default')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                selectedPalette === 'default'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🌈 Default (Vibrant)
            </button>
            <button
              onClick={() => setSelectedPalette('pastels')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                selectedPalette === 'pastels'
                  ? 'bg-pink-400 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🎀 Pastels (Soft & Gentle)
            </button>
            <button
              onClick={() => setSelectedPalette('earthtones')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                selectedPalette === 'earthtones'
                  ? 'bg-amber-700 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🌿 Earth Tones (Warm & Grounded)
            </button>
          </div>

          {/* Color Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-4 font-bold text-gray-900 border-b-2 border-gray-200">
                    Life Domain
                  </th>
                  <th className="text-left p-4 font-bold text-gray-900 border-b-2 border-gray-200">
                    Color Preview
                  </th>
                  <th className="text-left p-4 font-bold text-gray-900 border-b-2 border-gray-200">
                    Hex Code
                  </th>
                </tr>
              </thead>
              <tbody>
                {colorPalettes[selectedPalette].map((domain, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-4 font-medium text-gray-900">{domain.name}</td>
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-16 h-10 rounded-lg shadow-md border-2 border-gray-200"
                          style={{ backgroundColor: domain.color }}
                        ></div>
                      </div>
                    </td>
                    <td className="p-4">
                      <code className="bg-gray-100 px-3 py-1 rounded font-mono text-sm text-gray-800">
                        {domain.color}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-gray-700">
              <strong>💡 Quick Tip:</strong> Copy any hex code above and paste it into the color field on the Customize page! 
              You can also click the color picker to choose from millions of colors.
            </p>
          </div>
        </Card>

        {/* Quick Start Guide */}
        <Card className="p-8 mb-8 bg-white shadow-xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">🚀 Quick Start Steps</h2>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Navigate to Customize Page</h3>
                <p className="text-gray-600 text-sm">Click the "Customize" link in the navigation menu or use the button below.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Select Your Base Domain</h3>
                <p className="text-gray-600 text-sm">
                  Use the dropdown labeled "Base Domain (Questions Source)" to choose which questions you want.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Create Your Custom Name</h3>
                <p className="text-gray-600 text-sm">
                  Type your personalized domain name in the "Custom Domain Name (Display)" field.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                4
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Choose Your Color</h3>
                <p className="text-gray-600 text-sm">
                  Click the color box or enter a hex code. Check the live preview to see your wheel update in real-time!
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                5
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Save & Apply</h3>
                <p className="text-gray-600 text-sm">
                  Hit the "Save & Apply to Assessment" button. Your custom wheel is now ready for your next assessment!
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Call to Action */}
        <div className="text-center">
          <Card className="p-8 bg-gradient-to-r from-green-600 to-green-700 text-white shadow-2xl">
            <CheckCircle className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">Ready to Make It Yours?</h2>
            <p className="text-green-50 mb-6 max-w-2xl mx-auto">
              Your life is unique, and your wheel should be too! Start customizing now and create 
              a reflection tool that truly speaks to your journey. 🌟
            </p>
            <Link to="/customize">
              <Button className="bg-white text-green-600 hover:bg-gray-100 font-bold px-8 py-6 text-lg shadow-xl">
                Start Customizing Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </Card>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8 text-gray-600 text-sm">
          <p>Need help? Have questions? We're here to support your journey! 💚</p>
        </div>
      </div>
    </div>
  );
};

export default CustomizeGuidePage;
